import { createSlice } from '@reduxjs/toolkit';
import { getDeploymentRequests, getDeploymentRequestsSummary } from 'api/deployment-requests';

const initialState = {
    list: [],
    total: 0,
    error: '',
    isLoading: false,
    rowsPerPage: 10,
    currentPage: 1,
    searchQuery: '',
    summary: {
        pending: 0,
        approved: 0,
        declined: 0,
        isLoading: false,
        error: ''
    }
};

const slice = createSlice({
    name: 'deploymentRequests',
    initialState,
    reducers: {
        setDeploymentRequestsList(state, action) {
            state.list = action.payload?.data || [];
            state.total = action.payload?.total || 0;
            state.error = '';
            state.isLoading = false;
        },
        setIsLoading(state, action) {
            state.isLoading = action.payload;
            if (action.payload) state.error = '';
        },
        setError(state, action) {
            state.error = action.payload || 'Unable to fetch deployment requests.';
            state.list = [];
            state.total = 0;
            state.isLoading = false;
        },
        setDeploymentRequestsCurrentPage(state, action) {
            state.currentPage = action.payload;
        },
        setDeploymentRequestsRowsPerPage(state, action) {
            state.rowsPerPage = action.payload;
            state.currentPage = 1;
        },
        setSearchQuery(state, action) {
            state.currentPage = 1;
            state.searchQuery = action.payload || '';
        },
        setSummaryLoading(state, action) {
            state.summary.isLoading = action.payload;
            if (action.payload) state.summary.error = '';
        },
        setSummary(state, action) {
            state.summary = {
                pending: action.payload?.pending ?? action.payload?.active ?? 0,
                approved: action.payload?.approved || 0,
                declined: action.payload?.declined || 0,
                isLoading: false,
                error: ''
            };
        },
        setSummaryError(state, action) {
            state.summary.isLoading = false;
            state.summary.error = action.payload || 'Unable to fetch deployment request summary.';
        }
    }
});

export default slice.reducer;

export function fetchDeploymentRequestsList() {
    return async (dispatch, getState) => {
        try {
            dispatch(slice.actions.setIsLoading(true));
            const { currentPage, rowsPerPage, searchQuery } = getState().deploymentRequests;
            const response = await getDeploymentRequests({
                pageNo: currentPage,
                perPage: rowsPerPage,
                query: searchQuery
            });

            dispatch(slice.actions.setDeploymentRequestsList(response?.data));
        } catch (err) {
            console.error(err);
            dispatch(slice.actions.setError(err?.message));
        }
    };
}

export function searchDeploymentRequestsList(query) {
    return async (dispatch) => {
        dispatch(slice.actions.setSearchQuery(query));
        await dispatch(fetchDeploymentRequestsList());
    };
}

export function deploymentRequestsCurrentPage(page) {
    return async (dispatch) => {
        dispatch(slice.actions.setDeploymentRequestsCurrentPage(page));
        await dispatch(fetchDeploymentRequestsList());
    };
}

export function deploymentRequestsRowsPerPage(rowsPerPage) {
    return async (dispatch) => {
        dispatch(slice.actions.setDeploymentRequestsRowsPerPage(rowsPerPage));
        await dispatch(fetchDeploymentRequestsList());
    };
}

export function fetchDeploymentRequestsSummary() {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.setSummaryLoading(true));
            const response = await getDeploymentRequestsSummary();
            dispatch(slice.actions.setSummary(response?.data));
        } catch (err) {
            console.error(err);
            dispatch(slice.actions.setSummaryError(err?.message));
        }
    };
}
